import React from 'react';
import { accionDe, formatearMs, formatearUsd } from './metricas';
import type { Resultado } from './tipos';
import { ChipAccion, ChipError, colorFidelidad } from './ui';

/**
 * Lo que dijo el modelo sobre un caso y lo que DECIDIÓ el backend: fidelidad `n / max`
 * con color, badge de acción (OK / REGENERAR / PEDIR FOTOS) con sus motivos debajo,
 * resumen, un renglón por error (chip del tipo + descripción a la vista) y costo/latencia
 * chicos. `undefined` = todavía no hay fila (la corrida sigue). Compartido por el detalle
 * de Corridas y las dos columnas de Comparar (`compacto`).
 */
const ResultadoResumen: React.FC<{ resultado: Resultado | undefined; fidelidadMax: number; compacto?: boolean }> = ({
  resultado,
  fidelidadMax,
  compacto = false,
}) => {
  if (!resultado) {
    return <p className="animate-pulse text-xs text-gray-400">Pendiente de análisis…</p>;
  }
  if (resultado.error_analisis) {
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-red-600">Falló: {resultado.error_analisis}</p>
        <Pie r={resultado} />
      </div>
    );
  }
  const accion = accionDe(resultado);
  const motivos = resultado.decision?.motivos ?? [];
  return (
    <div className="flex gap-3">
      <div className="flex w-14 shrink-0 flex-col items-center">
        <span className={`${compacto ? 'text-2xl' : 'text-3xl'} font-bold leading-none ${colorFidelidad(resultado.fidelidad_producto, fidelidadMax)}`}>
          {resultado.fidelidad_producto ?? '?'}
        </span>
        <span className="text-[10px] text-gray-400">/ {fidelidadMax}</span>
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div>
          <ChipAccion accion={accion} />
          {motivos.length > 0 && (
            <ul className="mt-0.5">
              {motivos.map((m, i) => (
                <li key={`${m}-${String(i)}`} className="text-[10px] leading-4 text-gray-500">
                  {m}
                </li>
              ))}
            </ul>
          )}
        </div>
        {resultado.resumen && <p className="text-xs text-brand-black">{resultado.resumen}</p>}
        {resultado.instruccion_correccion && (
          <p className="rounded border-l-2 border-gray-300 bg-gray-50 py-1 pl-2 text-[11px] leading-4 text-gray-600">
            <span className="text-gray-400">Al regenerar: </span>
            {resultado.instruccion_correccion}
          </p>
        )}
        {resultado.errores.length > 0 ? (
          <ul className={compacto ? 'space-y-0.5' : 'space-y-1'}>
            {resultado.errores.map((e, i) => (
              <li key={`${e.id}-${String(i)}`} className="flex items-start gap-1.5">
                <ChipError error={e} />
                <span className="text-[11px] leading-4 text-gray-700">{e.descripcion}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11px] text-gray-400">Sin errores detectados.</p>
        )}
        <Pie r={resultado} />
      </div>
    </div>
  );
};

const Pie: React.FC<{ r: Resultado }> = ({ r }) => (
  <p className="text-[10px] text-gray-400">
    {formatearUsd(r.cost_usd)} · {formatearMs(r.duration_ms)}
    {r.tokens_in !== null || r.tokens_out !== null ? ` · ${r.tokens_in ?? '?'}→${r.tokens_out ?? '?'} tok` : ''}
    {r.modelo ? ` · ${r.modelo}` : ''}
  </p>
);

export default ResultadoResumen;
